import {
  Heading,
  Button,
  Icon,
  Avatar,
  AvatarFallback,
  useToast,
  GroupTextField,
  GroupTextFieldRow,
  Input,
} from "@elaniintc/goes-design-system";
import { useEffect, useState } from "react";
import ScrollModal from "@/components/common/scroll-modal";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TextAreaField } from "@/components/common/text-field";
import {
  useCreateTemplate,
  useUpdateTemplate,
} from "@/queries/templates/templates.api";
import type {
  DocumentDetails,
  TemplateCreatePayload,
  TemplateUpdateDetailsPayload,
} from "@/types/entities/templates";

interface NewDocumentModalProps {
  isOpen: boolean;
  template?: DocumentDetails;
  onClose: () => void;
  canDelete?: boolean;
  onDelete?: () => void;
  onSuccess?: () => void;
}

export default function NewDocumentModal({
  isOpen,
  template,
  onClose,
  canDelete = false,
  onDelete,
  onSuccess,
}: NewDocumentModalProps) {
  const isEditMode = !!template;
  const [templateName, setTemplateName] = useState("");
  const [description, setDescription] = useState("");
  const [contenido, setContenido] = useState("");

  const { showToast } = useToast();
  const { mutateAsync: createTemplate, isPending: isCreating } =
    useCreateTemplate();
  const { mutateAsync: updateTemplate, isPending: isUpdating } =
    useUpdateTemplate();

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (template) {
      setTemplateName(template.display_name);
      setDescription(template.description || "");
      setContenido(template.rich_text || "");
    } else {
      setTemplateName("");
      setDescription("");
    }
  }, [template, isOpen]);

  const isFormValid = templateName.trim() !== "" && templateName.length > 5;

  const handleReset = () => {
    setTemplateName("");
    setDescription("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (isEditMode && template) {
      const updateData: TemplateUpdateDetailsPayload = {
        display_name: templateName.trim(),
        description: description.trim(),
        mapping_name: "simple",
        rich_text: contenido,
      };

      await updateTemplate(
        { id: template.template_id, data: updateData },
        {
          onSuccess: () => {
            showToast({
              title: "Plantilla actualizada",
              description: "Los cambios se guardaron correctamente.",
              options: {
                type: "success",
                duration: 4000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="action" shape="square">
                      <Icon type="fas" name="check" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
            onSuccess?.();
            handleReset();
            onClose();
          },
          onError: () => {
            showToast({
              title: "Error al actualizar",
              description: "No se pudo actualizar la plantilla.",
              options: { type: "error", duration: 4000 },
            });
          },
        },
      );
    } else {
      const createData: TemplateCreatePayload = {
        display_name: templateName.trim(),
        description: description.trim(),
        mapping_name: "simple",
      };

      await createTemplate(createData, {
        onSuccess: () => {
          showToast({
            title: "Nueva plantilla creada",
            description:
              "La plantilla se generó correctamente. Puedes editarla o eliminarla.",
            options: {
              type: "success",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="action" shape="square">
                    <Icon type="fas" name="bell-on" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
          handleReset();
          onClose();
        },
        onError: (e) => {
          showToast({
            title: "Error al crear",
            description:
              e.response?.data?.message || "No se pudo crear la plantilla.",
            options: { type: "error", duration: 4000 },
          });
        },
      });
    }
  };

  return (
    <ScrollModal
      onClose={onClose}
      isOpen={isOpen}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle className="sr-only">
            {isEditMode ? "Editar Plantilla" : "Nueva Plantilla"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para {isEditMode ? "editar" : "crear"} una plantilla de
            documento
          </DialogDescription>
          <Heading variant="h5" className="!mb-0">
            {isEditMode ? "Editar Plantilla" : "Nueva Plantilla"}
          </Heading>
          <Button
            type="button"
            variant="quiet"
            aria-label="Cerrar"
            onClick={onClose}
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div
          className={`w-full grid py-3 gap-3 px-8 ${
            canDelete && onDelete && isEditMode
              ? "grid-cols-1 tablet:grid-cols-3"
              : "grid-cols-1 tablet:grid-cols-2"
          }`}
        >
          {canDelete && onDelete && isEditMode && (
            <Button
              variant="danger-tertiary"
              size="sm"
              type="button"
              onClick={onDelete}
              className="w-full"
            >
              Desactivar
            </Button>
          )}
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={onClose}
            className="w-full cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            disabled={!isFormValid || isSubmitting}
            form="template-form"
            className="w-full cursor-pointer"
          >
            {isEditMode ? "Guardar cambios" : "Crear plantilla"}
          </Button>
        </div>
      }
    >
      <form
        id="template-form"
        onSubmit={handleSubmit}
        data-testid="template-form"
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        <div className="flex flex-col gap-6 pb-6">
          <div>
            <Heading variant="h6" className="mb-4">
              Detalles
            </Heading>
            <div className="flex flex-col gap-4">
              <GroupTextField label="Nombre de plantilla" required>
                <GroupTextFieldRow>
                  <Input
                    className="w-full"
                    required
                    maxLength={120}
                    id="templateName"
                    name="templateName"
                    placeholder="Escribe el nombre de la plantilla"
                    value={templateName}
                    disabled={template?.status == "active"}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                </GroupTextFieldRow>
              </GroupTextField>

              <TextAreaField
                label="Descripción"
                helperText="Opcional"
                maxLength={250}
                rows={3}
                id="description"
                name="description"
                placeholder="Describe el contenido de la plantilla"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="resize-none w-full"
              />
            </div>
          </div>
        </div>
      </form>
    </ScrollModal>
  );
}
